import { useContext } from 'react'
import { Roles } from '@ucc/unified-admin-common'
import { useFlag } from '@unleash/proxy-client-react'

import { ConnectedUserContext, OrganizationContext } from 'components'
import { useAccountContext } from 'components/contexts/AccountContext'
import { BillinFeature } from 'types/BillingFeature'
import {
  CHANNEL_BOSS,
  CHANNEL_STERLING,
  CHANNEL_TITAN,
  COUNTRY_CODE_US,
  CURRENCY_IN,
  UNLEASH_FEATURE_FLAGS,
  VAT_COUNTRIES_SUPPORTED,
} from 'utils/constants'
import { getCaseInsensitiveCommerceSystem } from 'utils/subscriptionUtils'

const useBillingFeatures = (): BillinFeature => {
  const connectedUserContext = useContext(ConnectedUserContext)
  const organizationContext = useContext(OrganizationContext)
  const billingAdminUserManagementEnabled = useFlag(UNLEASH_FEATURE_FLAGS.billingAdminUserManagement)
  const { accountKey, billingRoles, licensesDetails } = useAccountContext()

  const billingInfo = organizationContext?.billingInfo

  const isAccountOnCommerceSystem =
    !!billingInfo?.commerceSystems &&
    (getCaseInsensitiveCommerceSystem(billingInfo?.commerceSystems, CHANNEL_BOSS) ||
      getCaseInsensitiveCommerceSystem(billingInfo?.commerceSystems, CHANNEL_TITAN) ||
      getCaseInsensitiveCommerceSystem(billingInfo?.commerceSystems, CHANNEL_STERLING))

  // Default Billing Admin
  const isBillingAdmin =
    (isAccountOnCommerceSystem &&
      (connectedUserContext?.accounts
        ?.find((account) => account.key === accountKey)
        ?.adminRoles?.includes(Roles.BillingAdmin) ||
        (billingAdminUserManagementEnabled &&
          !!connectedUserContext?.accounts?.find((account) => account.key === accountKey)?.billingRoles?.length))) ||
    false

  // For Sales Rep
  const systemUserHasBillingAccess =
    (isAccountOnCommerceSystem && connectedUserContext?.systemRoles?.includes(Roles.SystemUser)) || false

  // For Partner parent who has child access
  const partnerHasChildBillingAccess = (isAccountOnCommerceSystem && !!billingRoles?.length) || false

  const showPendingRenewal =
    isBillingAdmin && getCaseInsensitiveCommerceSystem(billingInfo?.commerceSystems, CHANNEL_TITAN)

  const showTaxExemption =
    isBillingAdmin &&
    ((billingInfo?.country === COUNTRY_CODE_US && licensesDetails?.products.includes('G2C')) ||
      VAT_COUNTRIES_SUPPORTED.includes(billingInfo?.country || ''))

  const showPaymentMethod = isBillingAdmin && billingInfo?.currency !== CURRENCY_IN

  // For both STERLING and BOSS users
  const showQuotes =
    (isBillingAdmin &&
      (getCaseInsensitiveCommerceSystem(billingInfo?.commerceSystems, CHANNEL_BOSS) ||
        getCaseInsensitiveCommerceSystem(billingInfo?.commerceSystems, CHANNEL_STERLING))) ||
    systemUserHasBillingAccess

  return {
    isAccountOnCommerceSystem,
    isBillingAdmin,
    systemUserHasBillingAccess,
    partnerHasChildBillingAccess,
    showPendingRenewal,
    showTaxExemption,
    showPaymentMethod,
    showQuotes,
  }
}

export default useBillingFeatures
